import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    TextField,
    Checkbox,
    FormControlLabel,
    Typography,
    CircularProgress,
    Alert,
    IconButton,
    Tooltip,
    Link,
    LinearProgress,
    Snackbar,
} from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'; // Icon for Auto-Create
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ImageSearchIcon from '@mui/icons-material/ImageSearch'; // Icon for Add Images
import io from 'socket.io-client'; // Import socket.io client
import MuiAlert from '@mui/material/Alert'; // Import Alert for Snackbar

import { 
    useListAiDrafts, 
    useGenerateAiDrafts, 
    useDeleteAiDraft, 
    useAutoCreateArticlesFromDrafts,
    useUpdateAiDraft, // Uncomment needed hook
    useListArticleFilters, // Uncomment needed hook
    useAddMissingDraftImages, // Import the new hook
} from '../services/appService';
import ConfirmDialog from './ConfirmDialog'; // For delete confirmation
import AiDraftEditModal from './AiDraftEditModal'; // Uncomment the edit modal import

// Alert component for Snackbar
const AlertSnackbar = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function AiDraftsModal({ open, onClose, onArticlesCreated, appId }) {
    const [drafts, setDrafts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [generateLoading, setGenerateLoading] = useState(false); // Separate loading for generation
    const [autoCreateLoading, setAutoCreateLoading] = useState(false); // Separate loading for auto-create
    const [addImagesLoading, setAddImagesLoading] = useState(false);
    const [error, setError] = useState('');
    const [prompt, setPrompt] = useState('');
    const [useRAG, setUseRAG] = useState(true);
    const [usePerplexity, setUsePerplexity] = useState(true);
    const [useImageSearch, setUseImageSearch] = useState(false); // Add state for image search, default false
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [draftToDelete, setDraftToDelete] = useState(null);
    const [editModalOpen, setEditModalOpen] = useState(false); // Uncomment edit state
    const [editingDraft, setEditingDraft] = useState(null); // Uncomment edit state
    const [autoCreateProgress, setAutoCreateProgress] = useState({ current: 0, total: 0, status: 'idle', message: '' }); // Progress state
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const socketRef = useRef(null); // Ref for the socket instance

    // Hooks
    const listDraftsHook = useListAiDrafts(appId);
    const generateDraftsHook = useGenerateAiDrafts();
    const deleteDraftHook = useDeleteAiDraft();
    const autoCreateHook = useAutoCreateArticlesFromDrafts();
    const updateDraftHook = useUpdateAiDraft();
    const listFiltersHook = useListArticleFilters(appId);
    const addMissingImagesHook = useAddMissingDraftImages(); // Initialize the hook

    const fetchDrafts = useCallback(async () => {
        if (!appId) return;
        setLoading(true);
        setError('');
        try {
            const data = await listDraftsHook();
            setDrafts(data || []);
        } catch (err) {
            console.error("Error fetching AI drafts:", err);
            setError(err.message || 'Failed to load AI drafts.');
            setDrafts([]);
        } finally {
            setLoading(false);
        }
    }, [appId, listDraftsHook]);

    useEffect(() => {
        if (open) {
            fetchDrafts();
            // Reset form state on open
            setPrompt('');
            setUseRAG(true);
            setUsePerplexity(true);
            setUseImageSearch(false); // Reset image search state
            setError('');
        }
    }, [open, fetchDrafts]);

    const handleGenerateDrafts = async () => {
        if (!prompt) {
            setError('Please enter a prompt or idea.');
            return;
        }
        setGenerateLoading(true);
        setError('');
        try {
            const result = await generateDraftsHook(appId, prompt, useRAG, usePerplexity, useImageSearch);
            console.log("Generate drafts result:", result);
            // Alert message based on result?
            if (result && result.message) {
                // Use a temporary success message or just refresh
                console.log(result.message);
            }
            fetchDrafts(); // Refresh the list
            setPrompt(''); // Clear prompt after generation
        } catch (err) {
            console.error("Error generating AI drafts:", err);
            setError(err.detail || err.message || 'Failed to generate drafts.');
        } finally {
            setGenerateLoading(false);
        }
    };

    const handleAutoCreateArticles = async () => {
        if (drafts.length === 0) return;
        setAutoCreateLoading(true);
        setError('');
        setAutoCreateProgress({ current: 0, total: drafts.length, status: 'requesting', message: 'Requesting auto-creation...' }); // Initial progress
        try {
            // Only *request* the process start, backend handles the rest
            await autoCreateHook(appId);
            // Don't reset loading or progress here, wait for socket events
             setSnackbarMessage('Auto-creation process initiated...');
             setSnackbarSeverity('info');
             setSnackbarOpen(true);
        } catch (err) {
            console.error("Error initiating auto-create articles:", err);
            setError(err.detail || err.message || 'Failed to start auto-create process.');
            setAutoCreateLoading(false); // Reset loading on initiation error
            setAutoCreateProgress({ current: 0, total: 0, status: 'idle', message: '' }); // Reset progress
        }
    };

    const handleAddMissingImages = async () => {
        setAddImagesLoading(true);
        setError('');
        try {
            const result = await addMissingImagesHook(appId);
            console.log("Add missing images result:", result);
            // Show feedback message
            if (result && result.message) {
                 alert(result.message); // Simple feedback for now
                 if (result.errors && result.errors.length > 0) {
                     console.error("Add images errors:", result.errors);
                     setError(`Image search completed with ${result.errors.length} errors. Check console.`);
                 }
            }
            fetchDrafts(); // Refresh the list to show new images
        } catch (err) {
            console.error("Error adding missing images:", err);
            setError(err.detail || err.message || 'Failed to add missing images.');
        } finally {
            setAddImagesLoading(false);
        }
    };

    // --- Delete Handling ---
    const handleOpenConfirmDialog = (id) => {
        setDraftToDelete(id);
        setConfirmDialogOpen(true);
    };

    const handleCloseConfirmDialog = () => {
        setDraftToDelete(null);
        setConfirmDialogOpen(false);
    };

    const handleDeleteConfirm = async () => {
        if (!draftToDelete) return;
        setLoading(true); // Use general loading for delete
        setError('');
        try {
            await deleteDraftHook(appId, draftToDelete);
            fetchDrafts(); // Refresh list
        } catch (err) {
            console.error("Error deleting draft:", err);
            setError(err.detail || err.message || 'Failed to delete draft.');
        } finally {
            handleCloseConfirmDialog();
            setLoading(false);
        }
    };

    // --- Edit Handling ---
    const handleOpenEditModal = (draft) => {
        console.log("Opening edit modal for draft:", draft);
        setEditingDraft(draft); // Set the draft to edit
        setEditModalOpen(true); // Open the modal
        // alert("Edit functionality not yet implemented."); // Remove alert
    };

    const handleCloseEditModal = () => {
        setEditModalOpen(false);
        setEditingDraft(null);
    };

    const handleSaveEdit = () => {
        fetchDrafts(); // Refresh the drafts list after saving
        handleCloseEditModal(); // Close the edit modal
    };

    // --- DataGrid Columns ---
    const columns = [
        { 
            field: 'title', 
            headerName: 'Title', 
            flex: 1.5, 
            minWidth: 200 
        },
        {
            field: 'body_md',
            headerName: 'Summary/Body',
            flex: 2,
            minWidth: 300,
            renderCell: (params) => (
                <Tooltip title={params.value || ''} arrow>
                    <Typography variant="body2" noWrap>{params.value}</Typography>
                </Tooltip>
            ),
        },
        { 
            field: 'draft_type', 
            headerName: 'Category', 
            width: 150
            // Future: Make this a dropdown using article_filters in Edit modal
        },
        {
            field: 'image_url',
            headerName: 'Image URL',
            width: 150,
            renderCell: (params) => (
                params.value ? 
                <Link href={params.value} target="_blank" rel="noopener noreferrer" underline="hover">View</Link> : 
                'N/A'
            ),
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 100,
            cellClassName: 'actions',
            getActions: ({ id, row }) => [
                <GridActionsCellItem
                    icon={<EditIcon />}
                    label="Edit"
                    onClick={() => handleOpenEditModal(row)} // Call the new handler
                    color="inherit"
                />,
                <GridActionsCellItem
                    icon={<DeleteIcon />}
                    label="Delete"
                    onClick={() => handleOpenConfirmDialog(id)}
                    color="inherit"
                />,
            ],
        },
    ];

    // --- Socket.IO Setup --- 
    useEffect(() => {
        if (open && appId && !socketRef.current) {
            // Connect to Socket.IO on default namespace
            console.log('[AiDraftsModal] Connecting to Socket.IO...');
            socketRef.current = io(undefined, {
                path: '/socket.io',
                reconnectionAttempts: 3,
            });

            const room = `app-${appId}`;

            socketRef.current.on('connect', () => {
                console.log(`[AiDraftsModal] Socket connected: ${socketRef.current.id}, joining room: ${room}`);
                socketRef.current.emit('joinRoom', room);
            });

            socketRef.current.on('disconnect', (reason) => {
                console.log(`[AiDraftsModal] Socket disconnected: ${reason}`);
            });

             socketRef.current.on('connect_error', (err) => {
                console.error('[AiDraftsModal] Socket connection error:', err);
                setError(prev => `${prev}\nSocket connection failed. Progress updates may not work.`);
            });

            // Listener for progress updates
            socketRef.current.on('autoCreateProgress', (data) => {
                console.log('[AiDraftsModal] Progress update:', data);
                 let message = '';
                 if (data.status === 'starting') message = 'Starting process...';
                 else if (data.status === 'processing_image') message = `Processing image for: ${data.draftTitle}`;
                 else if (data.status === 'generating_content') message = `Generating content for: ${data.draftTitle}`;
                 else if (data.status === 'saving') message = `Saving article for: ${data.draftTitle}`;
                 else if (data.status === 'success') message = `Successfully processed: ${data.draftTitle}`;
                 else if (data.status === 'error') message = `Error on ${data.draftTitle}: ${data.error}`;

                setAutoCreateProgress({
                    current: data.current || autoCreateProgress.current, // Keep previous current if not provided
                    total: data.total || autoCreateProgress.total, // Keep previous total if not provided
                    status: data.status,
                    message: message
                });
            });

            // Listener for completion
            socketRef.current.on('autoCreateComplete', (data) => {
                console.log('[AiDraftsModal] Auto-create complete:', data);
                 setSnackbarMessage(data.message || 'Auto-creation process finished.');
                 setSnackbarSeverity(data.errors && data.errors.length > 0 ? 'warning' : 'success');
                 setSnackbarOpen(true);
                 setAutoCreateProgress({ current: 0, total: 0, status: 'idle', message: '' }); // Reset progress
                 setAutoCreateLoading(false); // Ensure loading state is reset
                 fetchDrafts(); // Refresh drafts list
                 onArticlesCreated(); // Refresh articles list
            });

        } else if (!open && socketRef.current) {
            // Disconnect when modal closes
            console.log('[AiDraftsModal] Disconnecting socket...');
            socketRef.current.disconnect();
            socketRef.current = null;
        }

        // Cleanup on component unmount
        return () => {
            if (socketRef.current) {
                console.log('[AiDraftsModal] Cleaning up socket connection.');
                socketRef.current.disconnect();
                socketRef.current = null;
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, appId]); // Rerun if modal opens or appId changes

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarOpen(false);
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" scroll="paper">
                <DialogTitle>AI Draft Generation</DialogTitle>
                <DialogContent dividers>
                    {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
                    
                    {/* Drafts Table */}
                    <Box sx={{ height: 400, width: '100%', mb: 3 }}>
                        <Typography variant="h6" gutterBottom>Existing Drafts</Typography>
                        {loading ? (
                             <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                 <CircularProgress />
                             </Box>
                        ) : (
                            <DataGrid
                                rows={drafts}
                                columns={columns}
                                pageSizeOptions={[5, 10, 20]}
                                initialState={{
                                    pagination: {
                                        paginationModel: { page: 0, pageSize: 5 },
                                    },
                                }}
                                disableRowSelectionOnClick
                                // getRowId={(row) => row.id} // Ensure unique ID if not named 'id'
                                sx={{ border: 1, borderColor: 'divider' }}
                            />
                        )}
                    </Box>

                    {/* Generation Form */}
                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                        <Typography variant="h6" gutterBottom>Generate New Drafts</Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            variant="outlined"
                            label="Enter Topic, Idea, or URL..."
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            disabled={generateLoading}
                            sx={{ mb: 1 }}
                        />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', mb: 2, gap: 2 }}>
                            <Box sx={{display: 'flex', alignItems: 'center'}}>
                                <FormControlLabel
                                    control={<Checkbox checked={useRAG} onChange={(e) => setUseRAG(e.target.checked)} disabled={generateLoading} />}
                                    label="Use Knowledge Base (RAG)"
                                />
                                <Tooltip title="Enrich prompt with relevant info from your indexed documents.">
                                    <InfoOutlinedIcon fontSize="small" color="action" />
                                </Tooltip>
                            </Box>
                            <Box sx={{display: 'flex', alignItems: 'center'}}>
                                <FormControlLabel
                                    control={<Checkbox checked={usePerplexity} onChange={(e) => setUsePerplexity(e.target.checked)} disabled={generateLoading} />}
                                    label="Use Perplexity Sonar"
                                />
                                <Tooltip title="Enrich prompt with real-time web search results via Perplexity.">
                                    <InfoOutlinedIcon fontSize="small" color="action" />
                                </Tooltip>
                            </Box>
                            <Box sx={{display: 'flex', alignItems: 'center'}}>
                                <FormControlLabel
                                    control={<Checkbox checked={useImageSearch} onChange={(e) => setUseImageSearch(e.target.checked)} disabled={generateLoading} />}
                                    label="Image Search (Tavily)"
                                />
                                <Tooltip title="Attempt to find a relevant image URL for each generated draft using Tavily.">
                                    <InfoOutlinedIcon fontSize="small" color="action" />
                                </Tooltip>
                            </Box>
                        </Box>
                        <Button
                            variant="contained"
                            onClick={handleGenerateDrafts}
                            disabled={generateLoading || !prompt}
                            startIcon={generateLoading ? <CircularProgress size={20} /> : <AddIcon />}
                        >
                            Add AI Drafts
                        </Button>
                    </Box>

                    {/* Action Buttons Below Form */}
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}> 
                        {/* Add Missing Images Button */} 
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleAddMissingImages}
                            disabled={addImagesLoading || loading || generateLoading || autoCreateLoading}
                            startIcon={addImagesLoading ? <CircularProgress size={20} color="inherit" /> : <ImageSearchIcon />}
                        >
                            Add Missing Images
                        </Button>

                        {/* Auto-Create Section */} 
                        <Box sx={{ mt: 3, borderTop: 1, borderColor: 'divider', pt: 2 }}>
                             <Typography variant="h6" gutterBottom>Bulk Actions</Typography>
                            {drafts.length > 0 ? (
                                 <> 
                                     <Button
                                        variant="contained"
                                        color="error"
                                        onClick={handleAutoCreateArticles}
                                        disabled={autoCreateLoading || loading || generateLoading || addImagesLoading || autoCreateProgress.status !== 'idle'}
                                        startIcon={autoCreateLoading ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />}
                                        sx={{ mr: 2 }} // Add margin
                                    >
                                        Auto-Create Articles (Unpublished)
                                    </Button>
                                    {autoCreateLoading && (
                                         <Box sx={{ width: '100%', mt: 1 }}>
                                             <LinearProgress variant="determinate" value={(autoCreateProgress.current / (autoCreateProgress.total || 1)) * 100} />
                                             <Typography variant="caption" display="block" align="center" sx={{ mt: 0.5 }}>
                                                 {`Processing ${autoCreateProgress.current} / ${autoCreateProgress.total}... ${autoCreateProgress.message}`}
                                             </Typography>
                                         </Box>
                                    )}
                                 </>
                            ) : (
                                 <Typography variant="body2" color="textSecondary">No drafts available to auto-create articles.</Typography>
                            )}
                        </Box>
                    </Box>

                </DialogContent>
                <DialogActions sx={{ pb: 2, px: 3 }}>
                    <Button onClick={onClose} disabled={generateLoading || autoCreateLoading || loading || addImagesLoading}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */} 
            <ConfirmDialog
                open={confirmDialogOpen}
                onClose={handleCloseConfirmDialog}
                onConfirm={handleDeleteConfirm}
                title="Delete AI Draft?"
                message={`Are you sure you want to delete draft ID: ${draftToDelete}? This action cannot be undone.`}
            />

            {/* Edit Modal */}
            <AiDraftEditModal
                open={editModalOpen}
                onClose={handleCloseEditModal}
                onSave={handleSaveEdit} // Callback to refresh list on save
                draft={editingDraft}   // Pass the draft being edited
                appId={appId}
            />

            {/* Snackbar for feedback */}
            <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
                <AlertSnackbar onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </AlertSnackbar>
            </Snackbar>
        </>
    );
}

export default AiDraftsModal; 